import React, { useState, useEffect } from 'react';
import { apiService } from '../../config/api';
import { useSettings } from '../../context/SettingsContext';

const Settings = () => {
  // Get the refreshSettings function from our context
  const { refreshSettings } = useSettings();
  
  // State for different settings sections
  const [generalSettings, setGeneralSettings] = useState({
    siteName: 'Tafsir Art',
    tagline: 'The Art of Interpreting the Sacred',
    contactEmail: 'tafsirart@gmail.com',
    phoneNumber: '+27 64 130 4056',
    address: 'Johannesburg',
    businessHours: '',
    timezone: ''
  });
  
  const [languageSettings, setLanguageSettings] = useState({
    defaultLanguage: 'en',
    enablePortuguese: true,
    enableEnglish: true,
    enableArabic: true
  });
  
  const [paymentSettings, setPaymentSettings] = useState({
    currency: 'USD',
    enablePaypal: true,
    enableStripe: false,
    enableBankTransfer: true,
    paypalEmail: '',
    stripePublicKey: '',
    stripeSecretKey: '',
    bankAccountDetails: ''
  });
  
  const [securitySettings, setSecuritySettings] = useState({
    enableRateLimiting: true,
    maxRequestsPerMinute: 100,
    enableFirewall: false,
    blockedIPs: []
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Fetch settings from backend when component mounts
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const settings = await apiService.get('/settings');
      
      // Update state with fetched settings
      if (settings.general) setGeneralSettings(settings.general);
      if (settings.language) setLanguageSettings(settings.language);
      if (settings.payment) setPaymentSettings(settings.payment);
      if (settings.security) setSecuritySettings(settings.security);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to load settings. Please try again.' 
      });
      setLoading(false);
    }
  };
  
  const handleGeneralSettingsChange = (e) => {
    const { name, value } = e.target;
    setGeneralSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleLanguageSettingsChange = (e) => {
    const { name, type, checked, value } = e.target;
    setLanguageSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handlePaymentSettingsChange = (e) => {
    const { name, type, checked, value } = e.target;
    setPaymentSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSecuritySettingsChange = (e) => {
    const { name, type, checked, value } = e.target;
    setSecuritySettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const saveSettings = async (section, data) => {
    try {
      setLoading(true);
      await apiService.put(`/settings/${section}`, data);
      
      // Refresh settings in context so changes are available across the app
      await refreshSettings();
      
      setMessage({
        type: 'success',
        text: `${section.charAt(0).toUpperCase() + section.slice(1)} settings saved successfully!`
      });
      
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
      
      setLoading(false);
    } catch (error) {
      console.error(`Error saving ${section} settings:`, error);
      setMessage({
        type: 'error',
        text: `Failed to save ${section} settings. Please try again.`
      });
      setLoading(false);
    }
  };
  
  // Helper function to render form messages
  const renderMessage = () => {
    if (!message.text) return null;
    
    const bgColor = message.type === 'success' ? 'bg-green-100 border-green-500' : 'bg-red-100 border-red-500';
    const textColor = message.type === 'success' ? 'text-green-700' : 'text-red-700';
    
    return (
      <div className={`${bgColor} ${textColor} px-4 py-3 rounded relative border mb-6`} role="alert">
        <span className="block sm:inline">{message.text}</span>
      </div>
    );
  };
  
  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-primary">Website Settings</h1>
      
      {renderMessage()}
      
      {/* General Settings */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-xl font-semibold mb-4 text-primary">General Settings</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Site Name
            </label>
            <input
              type="text"
              name="siteName"
              value={generalSettings.siteName}
              onChange={handleGeneralSettingsChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tagline
            </label>
            <input
              type="text"
              name="tagline"
              value={generalSettings.tagline}
              onChange={handleGeneralSettingsChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Email
            </label>
            <input
              type="email"
              name="contactEmail"
              value={generalSettings.contactEmail}
              onChange={handleGeneralSettingsChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={generalSettings.phoneNumber}
              onChange={handleGeneralSettingsChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              type="text"
              name="address"
              value={generalSettings.address}
              onChange={handleGeneralSettingsChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Hours
            </label>
            <textarea
              name="businessHours"
              value={generalSettings.businessHours}
              onChange={handleGeneralSettingsChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Mon-Fri: 9AM-6PM&#10;Sat: 10AM-4PM&#10;Sun: Closed"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timezone
            </label>
            <select
              name="timezone"
              value={generalSettings.timezone}
              onChange={handleGeneralSettingsChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            >
              <option value="Africa/Johannesburg">Africa/Johannesburg (South Africa)</option>
              <option value="America/New_York">America/New_York (Eastern Time)</option>
              <option value="Europe/London">Europe/London (GMT)</option>
              <option value="Asia/Dubai">Asia/Dubai (UAE)</option>
              <option value="Asia/Riyadh">Asia/Riyadh (Saudi Arabia)</option>
            </select>
          </div>
          
          <button
            onClick={() => saveSettings('general', generalSettings)}
            disabled={loading}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:bg-gray-400"
          >
            {loading ? 'Saving...' : 'Save General Settings'}
          </button>
        </div>
      </div>
      
      {/* Language Settings */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-xl font-semibold mb-4 text-primary">Language Settings</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Language
            </label>
            <select
              name="defaultLanguage"
              value={languageSettings.defaultLanguage}
              onChange={handleLanguageSettingsChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            >
              <option value="en">English</option>
              <option value="pt">Portuguese</option>
              <option value="ar">Arabic</option>
            </select>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableEnglish"
              name="enableEnglish"
              checked={languageSettings.enableEnglish}
              onChange={handleLanguageSettingsChange}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="enableEnglish" className="ml-2 block text-sm text-gray-700">
              Enable English
            </label>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enablePortuguese"
              name="enablePortuguese"
              checked={languageSettings.enablePortuguese}
              onChange={handleLanguageSettingsChange}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="enablePortuguese" className="ml-2 block text-sm text-gray-700">
              Enable Portuguese
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableArabic"
              name="enableArabic"
              checked={languageSettings.enableArabic !== false}
              onChange={handleLanguageSettingsChange}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="enableArabic" className="ml-2 block text-sm text-gray-700">
              Enable Arabic
            </label>
          </div>
          
          <button
            onClick={() => saveSettings('language', languageSettings)}
            disabled={loading}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:bg-gray-400"
          >
            {loading ? 'Saving...' : 'Save Language Settings'}
          </button>
        </div>
      </div>
      
      {/* Payment Settings */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-xl font-semibold mb-4 text-primary">Payment Settings</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Currency
            </label>
            <select
              name="currency"
              value={paymentSettings.currency}
              onChange={handlePaymentSettingsChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            >
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="BRL">BRL - Brazilian Real</option>
            </select>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enablePaypal"
              name="enablePaypal"
              checked={paymentSettings.enablePaypal}
              onChange={handlePaymentSettingsChange}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="enablePaypal" className="ml-2 block text-sm text-gray-700">
              Enable PayPal
            </label>
          </div>
          
          {paymentSettings.enablePaypal && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PayPal Email
              </label>
              <input
                type="email"
                name="paypalEmail"
                value={paymentSettings.paypalEmail}
                onChange={handlePaymentSettingsChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>
          )}
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableStripe"
              name="enableStripe"
              checked={paymentSettings.enableStripe}
              onChange={handlePaymentSettingsChange}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="enableStripe" className="ml-2 block text-sm text-gray-700">
              Enable Stripe
            </label>
          </div>
          
          {paymentSettings.enableStripe && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stripe Public Key
                </label>
                <input
                  type="text"
                  name="stripePublicKey"
                  value={paymentSettings.stripePublicKey}
                  onChange={handlePaymentSettingsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stripe Secret Key
                </label>
                <input
                  type="password"
                  name="stripeSecretKey"
                  value={paymentSettings.stripeSecretKey}
                  onChange={handlePaymentSettingsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>
            </>
          )}
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableBankTransfer"
              name="enableBankTransfer"
              checked={paymentSettings.enableBankTransfer}
              onChange={handlePaymentSettingsChange}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="enableBankTransfer" className="ml-2 block text-sm text-gray-700">
              Enable Bank Transfer
            </label>
          </div>
          
          {paymentSettings.enableBankTransfer && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Account Details
              </label>
              <textarea
                name="bankAccountDetails"
                value={paymentSettings.bankAccountDetails}
                onChange={handlePaymentSettingsChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Bank name, account number, routing number, etc."
              ></textarea>
            </div>
          )}
          
          <button
            onClick={() => saveSettings('payment', paymentSettings)}
            disabled={loading}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:bg-gray-400"
          >
            {loading ? 'Saving...' : 'Save Payment Settings'}
          </button>
        </div>
      </div>
      
      {/* Security Settings */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-xl font-semibold mb-4 text-primary">Security Settings</h2>
        
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableRateLimiting"
              name="enableRateLimiting"
              checked={securitySettings.enableRateLimiting}
              onChange={handleSecuritySettingsChange}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="enableRateLimiting" className="ml-2 block text-sm text-gray-700">
              Enable Rate Limiting
            </label>
          </div>
          
          {securitySettings.enableRateLimiting && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Requests Per Minute
              </label>
              <input
                type="number"
                name="maxRequestsPerMinute"
                value={securitySettings.maxRequestsPerMinute}
                onChange={handleSecuritySettingsChange}
                min="10"
                max="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>
          )}
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableFirewall"
              name="enableFirewall"
              checked={securitySettings.enableFirewall}
              onChange={handleSecuritySettingsChange}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="enableFirewall" className="ml-2 block text-sm text-gray-700">
              Enable IP Blocking
            </label>
          </div>
          
          {securitySettings.enableFirewall && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Blocked IP Addresses (one per line)
              </label>
              <textarea
                name="blockedIPs"
                value={securitySettings.blockedIPs.join('\n')}
                onChange={(e) => {
                  const ips = e.target.value.split('\n').filter(ip => ip.trim() !== '');
                  setSecuritySettings(prev => ({
                    ...prev,
                    blockedIPs: ips
                  }));
                }}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Enter IP addresses to block, one per line"
              ></textarea>
            </div>
          )}
          
          <button
            onClick={() => saveSettings('security', securitySettings)}
            disabled={loading}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:bg-gray-400"
          >
            {loading ? 'Saving...' : 'Save Security Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
